import { useMutation, useQuery } from '@apollo/client';
import React, { useState } from 'react';
import { default as Form } from '../../components/connectedForm';
import { useProductConfig } from '../../utils/FetchConfig';
import getIn from '../connectedForm/utils/getIn';
import CheckboxInput from '../../components/connectedForm/CheckboxInput';
import getValue from '../connectedForm/utils/getValue';
import CustomerPriceMatrixNotificationActionQuery from '../../graphql/queries/actions/CustomerPriceMatrixNotificationActionQuery.graphql';
import CustomerPriceMatrixNotificationActionMutation from '../../graphql/mutations/actions/CustomerPriceMatrixNotificationActionMutation.graphql';
import normalizeVariables from '../../graphql/schema/normalizeVariables';
import { useProcessAlerts } from '../../utils/EntityMetaDataUtil';
import getClientMutationId from '../../utils/getClientMutationId';
import { BasicHeader } from '../../actions/BasicHeader';
import { ModalBottomButtonContainer } from './Styles';
import {
  ICustomerPriceMatrixActionParameters,
  ICustomerPriceMatrixNotificationAction,
  ICustomerPriceMatrixNotificationActionMutationResult,
  ICustomerPriceMatrixNotificationActionMutationVariables,
  ICustomerPriceMatrixNotificationActionQueryResult,
  ICustomerPriceMatrixNotificationActionQueryVariables,
  INotificationAction,
  IProductConfig,
  Maybe,
} from '../../graphql/schema/p4types';
import { SecondaryButton, SuccessButton } from '../connectedForm/Button';
import { NumberRange } from '../../@types/numberRange';
import FluentModal from './FluentModal';
import { useComponentQueryErrorHandling } from '../../utils/parseErrorResponse';

const PricingAttributeCheckboxField = ({
  attributeIndex,
  productConfig,
  customerNotification,
  customerPriceMatrixParameters,
  onChange,
}: {
  attributeIndex: NumberRange<0, 10>;
  productConfig: Maybe<IProductConfig>;
  customerNotification: Maybe<ICustomerPriceMatrixNotificationAction>;
  customerPriceMatrixParameters: ICustomerPriceMatrixActionParameters;
  onChange: (event: any, dataid: string) => void;
}) => {
  if (!productConfig || !customerNotification) return null;

  let title = productConfig[`pricingAttributeTitle${attributeIndex as NumberRange<1, 10>}`];
  if (!title) return null;

  const defaultParameters = customerNotification.customerPriceMatrixActionParameters;

  //Check that values exist for these attributes in any of the product(s) being updated.
  let includeAttribute = false;
  if (customerNotification && customerNotification.customerPriceMatrixPotentialList?.length! > 1) {
    for (let i = 0; i < customerNotification.customerPriceMatrixPotentialList?.length!; i++) {
      if (
        getIn(
          customerNotification,
          `customerPriceMatrixPotentialList.${i}.product.pricingAttributes.pricingAttribute${attributeIndex}.description`
        )
      ) {
        includeAttribute = true;
        break;
      }
    }
  } else {
    const attributeValue = getIn(
      customerNotification,
      `customerPriceMatrixPotentialList.0.product.pricingAttributes.pricingAttribute${attributeIndex}.description`
    );
    title += attributeValue ? ` (${attributeValue})` : '';
    includeAttribute = !!attributeValue;
  }
  if (!includeAttribute) return null;

  const readOnly = !!(
    defaultParameters?.[`pricingAttribute${attributeIndex as NumberRange<1, 10>}`] || defaultParameters?.targetProduct
  );

  return (
    <CheckboxInput
      data-testid={`pricingAttribute${attributeIndex}`}
      label={title}
      value={
        !!(
          customerPriceMatrixParameters[`pricingAttribute${attributeIndex as NumberRange<1, 10>}`] ||
          customerPriceMatrixParameters.targetProduct
        )
      }
      readOnly={readOnly}
      hint={readOnly ? 'An existing matrix has been found, selection cannot be changed.' : undefined}
      onChange={(event: any) => {
        onChange(event, `pricingAttribute${attributeIndex}`);
      }}
    />
  );
};

const CustomerPriceMatrixModal = ({
  onDismiss,
  notification,
  modalOpen,
}: {
  onDismiss: () => void;
  notification: INotificationAction;
  modalOpen: boolean;
}) => {
  const [values, setValues] = useState<ICustomerPriceMatrixActionParameters>({});
  const [loading, setLoading] = useState<boolean>(false);

  const productConfig = useProductConfig();

  const handleChange = (event: any, dataid: string) => {
    const value = getValue(event);

    const newValues = { ...values };
    if (dataid !== 'targetProduct' && dataid !== 'baseOnHistoricCost') {
      newValues.targetProduct = false;
    }
    newValues[dataid as keyof typeof newValues] = value;

    setValues(newValues);
  };

  const processAlerts = useProcessAlerts();
  const onCompletedCreateMutation = (response: ICustomerPriceMatrixNotificationActionMutationResult) => {
    setLoading(false);
    //Check response for further alerts (Typically expecting a success notification, but could involve further actions, etc).
    processAlerts(
      getIn(response, 'customerPriceMatrixNotificationActionMutation.result.entityMetaData'),
      'Customer Price Matrix'
    );
    onDismiss();
  };

  const { data: customerPriceMatrixNotificationActionData } = useQuery<
    ICustomerPriceMatrixNotificationActionQueryResult,
    ICustomerPriceMatrixNotificationActionQueryVariables
  >(CustomerPriceMatrixNotificationActionQuery, {
    variables: { id: notification.id },
    skip: !modalOpen, //Defer query until initial notification handled by user, otherwise we may fetch out-of-date information.
    fetchPolicy: 'network-only',
    onCompleted: (response) => {
      const defaultParameters = response?.data
        ?.customerPriceMatrixActionParameters as Maybe<ICustomerPriceMatrixActionParameters>;

      if (defaultParameters !== null && defaultParameters !== undefined) {
        setValues(defaultParameters);
      }
    },
  });

  const handleQueryError = useComponentQueryErrorHandling();

  const [createCustomerPriceMatrix] = useMutation<
    ICustomerPriceMatrixNotificationActionMutationResult,
    ICustomerPriceMatrixNotificationActionMutationVariables
  >(CustomerPriceMatrixNotificationActionMutation, {
    onCompleted: onCompletedCreateMutation,
    onError: (data) => {
      setLoading(false);
      handleQueryError(data);
    },
  });

  const customerNotification =
    customerPriceMatrixNotificationActionData?.data as Maybe<ICustomerPriceMatrixNotificationAction>;
  const isCustomer = customerNotification?.selectedOption?.includes('Customer Account');

  let title;
  if (!customerNotification) {
    title = `New Matrix`;
  } else if (isCustomer) {
    title = `New Matrix for Customer ${customerNotification.customer?.name}`;
  } else {
    title = `New Matrix for Price Source ${customerNotification.customerPriceSource?.name}`;
  }

  const targetProductReadonly = !!customerNotification?.customerPriceMatrixActionParameters?.targetProduct;

  return (
    <FluentModal isOpen={modalOpen} styles={{ main: { minWidth: '500px', paddingBottom: '60px' } }}>
      <BasicHeader
        secondaryTitle={`Select attributes - If no selections are made, matrix may affect all products for this ${
          isCustomer ? 'customer' : 'customer price source'
        }`}
      >
        {title}
      </BasicHeader>
      <div style={{ padding: '1em', marginBottom: '11px' }}>
        <Form.Fieldset cols={1}>
          <PricingAttributeCheckboxField
            attributeIndex={0}
            productConfig={productConfig}
            customerNotification={customerNotification}
            customerPriceMatrixParameters={values}
            onChange={handleChange}
          />
          <PricingAttributeCheckboxField
            attributeIndex={1}
            productConfig={productConfig}
            customerNotification={customerNotification}
            customerPriceMatrixParameters={values}
            onChange={handleChange}
          />
          <PricingAttributeCheckboxField
            attributeIndex={2}
            productConfig={productConfig}
            customerNotification={customerNotification}
            customerPriceMatrixParameters={values}
            onChange={handleChange}
          />
          <PricingAttributeCheckboxField
            attributeIndex={3}
            productConfig={productConfig}
            customerNotification={customerNotification}
            customerPriceMatrixParameters={values}
            onChange={handleChange}
          />
          <PricingAttributeCheckboxField
            attributeIndex={4}
            productConfig={productConfig}
            customerNotification={customerNotification}
            customerPriceMatrixParameters={values}
            onChange={handleChange}
          />
          <PricingAttributeCheckboxField
            attributeIndex={5}
            productConfig={productConfig}
            customerNotification={customerNotification}
            customerPriceMatrixParameters={values}
            onChange={handleChange}
          />
          <PricingAttributeCheckboxField
            attributeIndex={6}
            productConfig={productConfig}
            customerNotification={customerNotification}
            customerPriceMatrixParameters={values}
            onChange={handleChange}
          />
          <PricingAttributeCheckboxField
            attributeIndex={7}
            productConfig={productConfig}
            customerNotification={customerNotification}
            customerPriceMatrixParameters={values}
            onChange={handleChange}
          />
          <PricingAttributeCheckboxField
            attributeIndex={8}
            productConfig={productConfig}
            customerNotification={customerNotification}
            customerPriceMatrixParameters={values}
            onChange={handleChange}
          />
          <PricingAttributeCheckboxField
            attributeIndex={9}
            productConfig={productConfig}
            customerNotification={customerNotification}
            customerPriceMatrixParameters={values}
            onChange={handleChange}
          />
          <PricingAttributeCheckboxField
            attributeIndex={10}
            productConfig={productConfig}
            customerNotification={customerNotification}
            customerPriceMatrixParameters={values}
            onChange={handleChange}
          />
          <CheckboxInput
            data-testid={`targetProduct`}
            label={'Product'}
            value={!!values.targetProduct}
            onChange={(event: any) => handleChange(event, `targetProduct`)}
            readOnly={targetProductReadonly}
            hint={targetProductReadonly ? 'An existing matrix has been found, selection cannot be changed.' : undefined}
          />
          <CheckboxInput
            data-testid={`baseOnHistoricCost`}
            label={'Historic costs'}
            value={!!values.baseOnHistoricCost}
            onChange={(event: any) => handleChange(event, `baseOnHistoricCost`)}
          />
        </Form.Fieldset>
      </div>
      <ModalBottomButtonContainer>
        <SecondaryButton onClick={onDismiss}>Cancel</SecondaryButton>
        <SuccessButton
          onClick={() => {
            setLoading(true);
            createCustomerPriceMatrix({
              variables: {
                input: {
                  clientMutationId: getClientMutationId(),
                  action: normalizeVariables(customerNotification),
                  parameters: normalizeVariables(values),
                  disableConfirmations: true,
                },
              },
            });
          }}
          disabled={loading}
        >
          Create Matrix
        </SuccessButton>
      </ModalBottomButtonContainer>
    </FluentModal>
  );
};

export default CustomerPriceMatrixModal;
